import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import LeadVideoModal from './LeadVideoModal';
import VideoReminderPopup from './VideoReminderPopup';
import { getCustomerVideo } from '../../Services/videoService';

// Fired by the dashboard once the customer dismisses the VJ Loan Assist banner.
// Using an event rather than props keeps this component mountable at the layout
// level, so the reminder follows the customer onto every page.
export const LOAN_BANNER_CLOSED_EVENT = 'vj:loan-banner-closed';

// Fired by the side menu's "Videos" item so the customer can revisit the series
// at any time, including after they have watched all of it.
export const OPEN_VIDEOS_EVENT = 'vj:open-lead-videos';

// Broadcast so the side menu knows whether to show its "Videos" item at all.
// The value is mirrored onto window because the menu may mount either side of
// this component's first fetch, and an event alone would be missed.
export const VIDEOS_AVAILABLE_EVENT = 'vj:lead-videos-available';
export const VIDEO_COUNT_GLOBAL = '__vjLeadVideoCount';

/**
 * Owns the onboarding video series for the whole authenticated app.
 *
 * Mounted once in the Mainpage layout, so the reminder appears on Tokens,
 * Allotments, Transactions — anywhere — not only on the dashboard.
 */
const LeadVideoNudge = () => {
  const loggedInUser = useSelector((state) => state.Auth.user);
  const leadId = loggedInUser && loggedInUser.leadId;

  const [videos, setVideos] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [resumeSequence, setResumeSequence] = useState(null);
  const [startSequence, setStartSequence] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAtOpen, setPendingAtOpen] = useState(0);
  const [autoStart, setAutoStart] = useState(false);

  const refresh = useCallback(async () => {
    if (!leadId) return null;
    const result = await getCustomerVideo(leadId);
    const count = result && result.videos ? result.videos.length : 0;
    window[VIDEO_COUNT_GLOBAL] = count;
    window.dispatchEvent(
      new CustomEvent(VIDEOS_AVAILABLE_EVENT, { detail: { count: count } })
    );

    if (!result || !result.videos || !result.videos.length) {
      setVideos([]);
      setPendingCount(0);
      setResumeSequence(null);
      return null;
    }
    setVideos(result.videos);
    setPendingCount(result.pendingCount);
    setResumeSequence(result.resumeSequence);
    return result;
  }, [leadId]);

  // Load once the customer is known, so the reminder can appear on whichever
  // page they happen to be on.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // The dashboard tells us when the loan banner has gone; that is the moment
  // the series is allowed to take over the screen.
  useEffect(() => {
    const onBannerClosed = async () => {
      const result = await refresh();
      if (result && result.shouldShow) {
        setStartSequence(result.resumeSequence);
        setAutoStart(false);
        setPendingAtOpen(result.pendingCount);
        setModalOpen(true);
      }
    };
    window.addEventListener(LOAN_BANNER_CLOSED_EVENT, onBannerClosed);
    return () => window.removeEventListener(LOAN_BANNER_CLOSED_EVENT, onBannerClosed);
  }, [refresh]);

  const handleModalClose = async () => {
    setModalOpen(false);
    await refresh();
  };

  const handleResume = () => {
    setStartSequence(resumeSequence);
    setAutoStart(false);
    setPendingAtOpen(pendingCount);
    setModalOpen(true);
  };

  // Opened from the side menu. Lands on the picker so the customer chooses a
  // video, and once everything has been watched it becomes replay-only.
  useEffect(() => {
    const onOpenRequest = async (event) => {
      const result = await refresh();
      if (!result || !result.videos || !result.videos.length) return;

      // The Tutorials page names the video the customer clicked; anything else
      // (the side menu, the reminder) opens the picker as before.
      const asked = event && event.detail && Number(event.detail.sequence);
      const wanted = Number.isInteger(asked)
        ? result.videos.find(function (v) { return v.sequence === asked; })
        : null;

      setStartSequence(wanted ? wanted.sequence : result.resumeSequence);
      setAutoStart(!!wanted);
      setPendingAtOpen(result.pendingCount);
      setModalOpen(true);
    };
    window.addEventListener(OPEN_VIDEOS_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_VIDEOS_EVENT, onOpenRequest);
  }, [refresh]);

  if (!leadId) return null;

  return (
    <>
      <LeadVideoModal
        open={modalOpen}
        onClose={handleModalClose}
        videos={videos}
        startSequence={startSequence}
        leadId={leadId}
        // Still something to watch -> keep recording. Everything watched -> a
        // pure replay, and the history stays exactly as it was.
        trackProgress={pendingAtOpen > 0}
        autoStart={autoStart}
      />
      <VideoReminderPopup
        active={pendingCount > 0 && !modalOpen}
        remaining={pendingCount}
        total={videos.length}
        onResume={handleResume}
      />
    </>
  );
};

export default LeadVideoNudge;
